/** Pure URL helpers for the console-wide tenant scope.
 *
 * Kept free of React and of the store so the URL grammar can be unit-tested on
 * its own — it is the part shared by the router sync (`TenantScopeUrlSync`) and
 * the anchor patch (`useScopeAwareAnchors`), which operate on very different
 * inputs (a `URLSearchParams` vs a raw `href` attribute).
 *
 * The scope is a list of operators: `?scope=12,845`, ids ascending (plan D1).
 * One id is a one-element list, so `?scope=12` links keep working.
 */
import { formatTenantIds, parseTenantIds } from './tenantIds';

/** Query param carrying the console-wide scope. NOT `tenant` — see the plan:
 *  the scope is a layer UNDER the per-page `tenant` filter, and writing it into
 *  `tenant` would invert its precedence against saved presets. */
export const SCOPE_PARAM = 'scope';

/** Per-page override meaning "all tenants here, despite the scope".
 *
 *  A page's tenant filter cannot simply be deleted while a scope is active —
 *  deleting it falls straight back to the scope layer, so the picker would be
 *  un-clearable. This sentinel occupies the URL param without selecting a
 *  tenant. It is inert for the API: `useGridFilters` drops it before `toApi`,
 *  and every existing `toApi` would drop it anyway (they all return undefined
 *  for a value that is neither digits nor their own sentinel). */
export const TENANT_ALL = 'all';

/** Read the scope's operator ids out of a query string / URLSearchParams,
 *  sorted and de-duplicated; empty when there is none. Tokens that are not
 *  positive ids are ignored — the param is hand-editable. */
export function readScope(search: string | URLSearchParams): number[] {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return parseTenantIds(params.get(SCOPE_PARAM));
}

/** True when this href points somewhere inside this console (same origin, and
 *  not a hash-only / mailto: / tel: / download link). */
export function isInternalHref(href: string, origin: string): boolean {
  if (!href) return false;
  if (href.startsWith('#')) return false;
  try {
    const u = new URL(href, origin);
    if (u.origin !== origin) return false;
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Add `?scope=<ids>` to an href. Returns the href unchanged when it already
 *  carries a scope (an explicit one always wins), when the href is not
 *  internal, or when there are no ids. */
export function withScope(
  href: string,
  ids: number | readonly number[] | null,
  origin: string,
): string {
  const value = formatTenantIds(ids === null ? [] : typeof ids === 'number' ? [ids] : ids);
  if (value === null) return href;
  if (!isInternalHref(href, origin)) return href;
  const u = new URL(href, origin);
  if (u.searchParams.has(SCOPE_PARAM)) return href;
  u.searchParams.set(SCOPE_PARAM, value);
  // Keep it relative when the input was relative, so React Router and the
  // address bar both stay tidy.
  return /^[a-z]+:/i.test(href) || href.startsWith('//') ? u.href : `${u.pathname}${u.search}${u.hash}`;
}

/** Remove the scope param (used when the scope is reset). */
export function stripScope(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(SCOPE_PARAM);
  return next;
}
